/**
 * PatternLearningDialog - Learn patterns from user corrections
 * Shows matching rows and allows selective application
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Lightbulb, Search } from 'lucide-react';
import { BankStatementRow } from '../../stores/bankEntryStore';

const API_BASE = import.meta.env.VITE_API_URL;

interface PatternLearningDialogProps {
  open: boolean;
  onClose: () => void;
  editedRow: BankStatementRow;
  newLedger: string;
  allRows: BankStatementRow[];
  selectedCompany: string;
  onApply: (rowIndices: number[], pattern: string) => void;
}

/**
 * Extract suggested pattern from narration
 * Removes common prefixes and finds significant keywords
 */
function suggestPattern(narration: string): string {
  if (!narration) return '';

  let text = narration.toUpperCase();

  // Remove common transaction prefixes
  const prefixes = [
    'UPI/', 'NEFT/', 'IMPS/', 'RTGS/',
    'TO ', 'FROM ', 'BY ', 'BY-',
    'CR-', 'DR-', 'TRF-', 'CHQ-',
    'ACH/', 'ECS/', 'NACH/',
  ];

  for (const prefix of prefixes) {
    if (text.startsWith(prefix)) {
      text = text.slice(prefix.length);
      break;
    }
  }

  // Split by common delimiters and find first significant word
  const parts = text.split(/[\/\-\s@]+/);

  for (const part of parts) {
    // Skip numbers, dates, and very short strings
    const cleaned = part.replace(/[^A-Z]/g, '');
    if (cleaned.length >= 3 && !/^\d+$/.test(part)) {
      // Return first 2-3 words for better matching
      const significantParts = parts
        .filter(p => p.replace(/[^A-Z]/g, '').length >= 3 && !/^\d+$/.test(p))
        .slice(0, 2);
      return significantParts.join(' ');
    }
  }

  return '';
}

export const PatternLearningDialog: React.FC<PatternLearningDialogProps> = ({
  open,
  onClose,
  editedRow,
  newLedger,
  allRows,
  selectedCompany,
  onApply,
}) => {
  const [pattern, setPattern] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Initialize pattern suggestion when dialog opens
  useEffect(() => {
    if (open && editedRow) {
      const suggested = suggestPattern(editedRow.narration);
      setPattern(suggested);
    }
  }, [open, editedRow]);

  // Find matching rows based on pattern
  const matchingRows = useMemo(() => {
    if (!pattern || pattern.length < 2) return [];

    const patternUpper = pattern.toUpperCase();
    return allRows.filter(row =>
      row.rowIndex !== editedRow.rowIndex && // Exclude the edited row
      row.narration.toUpperCase().includes(patternUpper) &&
      row.ledger !== newLedger // Only show rows not already mapped to this ledger
    );
  }, [pattern, allRows, editedRow.rowIndex, newLedger]);

  // Auto-select all matching rows when pattern changes
  useEffect(() => {
    setSelectedRows(new Set(matchingRows.map(r => r.rowIndex)));
  }, [matchingRows]);

  const handleToggleRow = (rowIndex: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === matchingRows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(matchingRows.map(r => r.rowIndex)));
    }
  };

  const handleApply = async () => {
    setIsSaving(true);

    try {
      // Save pattern to backend (include patternId for UPDATE, omit for INSERT)
      const token = localStorage.getItem('auth_token');
      const payload = {
        company: selectedCompany,
        pattern: pattern,
        ledger: newLedger,
        narration: editedRow.narration,
        patternId: editedRow.patternId,  // If exists, backend UPDATEs; else INSERTs
      };
      await fetch(`${API_BASE}/api/bank-entry/learn-pattern`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      // Apply to selected rows (includes the edited row)
      const rowsToUpdate = [editedRow.rowIndex, ...Array.from(selectedRows)];
      onApply(rowsToUpdate, pattern);
      onClose();
    } catch (error) {
      console.error('Failed to save pattern:', error);
      // Still apply locally even if backend fails
      const rowsToUpdate = [editedRow.rowIndex, ...Array.from(selectedRows)];
      onApply(rowsToUpdate, pattern);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    // Just update the single row without learning
    onApply([editedRow.rowIndex], '');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            Learn Pattern
          </DialogTitle>
          <DialogDescription>
            {newLedger.toLowerCase().includes('suspense')
              ? 'Group similar transactions for batch resolution later'
              : 'Teach the system to recognize similar transactions automatically'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto overflow-x-hidden">
          {/* Original narration */}
          <div className="p-3 bg-muted rounded-lg">
            <Label className="text-xs text-muted-foreground">Narration</Label>
            <p className="text-sm font-mono mt-1 break-words whitespace-normal">{editedRow.narration}</p>
          </div>

          {/* New ledger */}
          <div className="flex items-start gap-2">
            <Label className="flex-shrink-0">Mapped to:</Label>
            <Badge variant="secondary" className="text-sm break-words whitespace-normal max-w-full">{newLedger}</Badge>
          </div>

          {/* Pattern input */}
          <div className="space-y-2">
            <Label htmlFor="pattern" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Pattern to match
            </Label>
            <Input
              id="pattern"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Enter keyword that identifies this ledger..."
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              This pattern will be used to auto-classify future transactions
            </p>
          </div>

          {/* Matching rows */}
          {matchingRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Apply to similar rows ({matchingRows.length} found)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedRows.size === matchingRows.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              <ScrollArea className="h-48 border rounded-lg p-2">
                <div className="space-y-1">
                  {matchingRows.map((row) => (
                    <div
                      key={row.rowIndex}
                      className={`flex items-start gap-3 p-2 rounded hover:bg-muted cursor-pointer ${
                        selectedRows.has(row.rowIndex) ? 'bg-muted/50' : ''
                      }`}
                      onClick={() => handleToggleRow(row.rowIndex)}
                    >
                      <Checkbox
                        checked={selectedRows.has(row.rowIndex)}
                        onCheckedChange={() => handleToggleRow(row.rowIndex)}
                        className="mt-1 flex-shrink-0"
                      />
                      <span className="text-xs text-muted-foreground w-8 flex-shrink-0 mt-1">
                        #{row.rowIndex + 1}
                      </span>
                      <span className="flex-1 text-sm font-mono break-words whitespace-normal min-w-0">
                        {row.narration}
                      </span>
                      <span className="text-sm font-mono text-right w-20 flex-shrink-0">
                        {row.debit ? `₹${row.debit.toLocaleString()}` : ''}
                        {row.credit ? `₹${row.credit.toLocaleString()}` : ''}
                      </span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {row.debit ? 'Dr' : 'Cr'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {pattern && matchingRows.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No other rows match this pattern
            </p>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 flex gap-2 sm:gap-0 pt-4 border-t mt-4">
          <Button variant="ghost" onClick={handleSkip}>
            Skip - Just this row
          </Button>
          <Button onClick={handleApply} disabled={isSaving || !pattern}>
            {isSaving ? 'Saving...' : (
              selectedRows.size > 0
                ? `Apply to ${selectedRows.size + 1} rows & Learn`
                : 'Learn Pattern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PatternLearningDialog;
